"use client";

import Fuse from "fuse.js";
import { useMemo, useState } from "react";
import { useTranslations } from "@/i18n/client";
import { countries } from "@/i18n/routing";

type Country = (typeof countries)[number];

export const useCountrySearch = (selectedCountry: string) => {
	const t = useTranslations();
	const [query, setQuery] = useState("");

	const sortedCountries = useMemo(
		() =>
			[...countries].sort((a, b) => {
				if (a.country === selectedCountry) return -1;
				if (b.country === selectedCountry) return 1;
				return 0;
			}),
		[selectedCountry],
	);

	const fuse = useMemo(
		() =>
			new Fuse(
				countries.map((c) => ({
					original: c,
					label: t(`countries.${c.name}`),
					name: c.name,
					currency: c.currency,
					code: c.country,
				})),
				{
					keys: [
						{ name: "label", weight: 2 },
						{ name: "name", weight: 1 },
						{ name: "currency", weight: 0.5 },
						{ name: "code", weight: 0.5 },
					],
					threshold: 0.3,
					ignoreLocation: true,
					minMatchCharLength: 1,
				},
			),
		[t],
	);

	const results = useMemo<Country[]>(() => {
		const trimmed = query.trim();
		if (!trimmed) return sortedCountries;
		return fuse.search(trimmed).map((result) => result.item.original);
	}, [query, fuse, sortedCountries]);

	return { query, setQuery, results };
};
